import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTheme } from 'styled-components';
import { Check, ChevronRight } from 'react-feather';
import logo from '../../../../../assets/logo.png';

// Import refactored components
import Step1InstitutionProfile from './components/Step1InstitutionProfile';
import Step2ComplianceActivity from './components/Step2ComplianceActivity';
import Step3AuthorizedSigners from './components/Step3AuthorizedSigners';
import Step4Documentation from './components/Step4Documentation';

// Import Sardine hook
import useSardine from '../../../common/useSardine';

// Import styles
import {
    PageContainer, SidebarContainer, SidebarLogo, StepList, StepItem,
    StepIconWrapper, StepTextContent, StepLabel, StepDesc,
    ContentContainer, MobileHeader, MobileHeaderTitle, MobileProgressBar,
    MobileProgressFill, FormArea, ActionFooter, GhostButton, PrimaryButton,
    STEPS
} from './styles';

// --- VALIDATION SCHEMAS PER STEP ---
const step1Schema = yup.object().shape({
    institutionName: yup.string(),
    institutionType: yup.string(),
    registrationNumber: yup.string(),
    incorporationCountry: yup.string(),
    headquartersAddress: yup.string(),
    city: yup.string(),
    state: yup.string(),
    zipCode: yup.string(),
    companyPhoneDialCode: yup.string(),
    companyPhone: yup.string(),
    companyEmail: yup.string().email('Invalid email'),
    swiftBic: yup.string(),
});

const step2Schema = yup.object().shape({
    operatingCorridors: yup.string(),
    monthlyTransferVolume: yup.string(),
    amlPolicyStatus: yup.string(),
    sourceOfFunds: yup.string(),
    intendedUse: yup.string(),
    transactionTypes: yup.string(),
    transactionFrequency: yup.string(),
    averageBalance: yup.string(),
    primaryUseCases: yup.string(),
    website: yup.string().url('Must be a valid URL'),
});

const step3Schema = yup.object().shape({
    signers: yup.array().of(
        yup.object().shape({
            signerName: yup.string(),
            signerTitle: yup.string(),
            ownershipPercentage: yup.number()
                .transform((v, o) => o === '' ? undefined : v)
                .min(0, 'Min 0%').max(100, 'Max 100%'),
            signerDob: yup.string(),
            signerIdType: yup.string(),
            signerIdNumber: yup.string(),
            signerIdIssuingCountry: yup.string(),
            signerEmail: yup.string().email('Invalid email'),
            signerPhoneDialCode: yup.string(),
            signerPhone: yup.string(),
            signerAddress: yup.string(),
            signerCity: yup.string(),
            signerState: yup.string(),
            signerZipCode: yup.string(),
            signerCountry: yup.string(),
            isPEP: yup.boolean(),
            pepDetails: yup.string(),
        })
    )
    .min(1, 'At least one signer is required')
    .max(4, 'Maximum 4 signers allowed')
    .test('sum-ownership', 'Total ownership cannot exceed 100%', (val) => {
        if (!val) return true;
        const total = val.reduce((sum, item) => sum + (Number(item.ownershipPercentage) || 0), 0);
        return total <= 100;
    })
});

const step4Schema = yup.object().shape({
    agreeTerms: yup.boolean().oneOf([true], 'You must accept the terms to proceed'),
});

const schemas = [step1Schema, step2Schema, step3Schema, step4Schema];

const CrossBorderOnboarding = () => {
    const [currentStep, setCurrentStep] = useState(1);
    const navigate = useNavigate();
    const theme = useTheme();
    const { sessionKey } = useSardine();

    // Initialize React Hook Form
    const methods = useForm({
        resolver: yupResolver(schemas[currentStep - 1]),
        mode: 'onTouched',
        defaultValues: {
            companyPhoneDialCode: '+1',
            incorporationCountry: 'United States',
            agreeTerms: false,
            signers: [{
                signerName: '',
                signerTitle: '',
                ownershipPercentage: '',
                signerDob: '',
                signerIdType: '',
                signerIdNumber: '',
                signerIdIssuingCountry: '',
                signerEmail: '',
                signerPhoneDialCode: '+1',
                signerPhone: '',
                signerAddress: '',
                signerCity: '',
                signerState: '',
                signerZipCode: '',
                signerCountry: '',
                isPEP: false,
                pepDetails: ''
            }]
        }
    });

    const { handleSubmit, trigger } = methods;

    const saveDraft = async () => {
        const data = methods.getValues();
        try {
            await fetch('http://localhost:4000/api/kyc/draft', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('userToken')}`
                },
                body: JSON.stringify({
                    accountType: 'cross_border',
                    step: currentStep,
                    stepData: data,
                    sessionKey: sessionKey
                })
            });
            console.log('Draft saved successfully (Mock)');
        } catch (error) {
            console.error('Failed to save draft (expected in mock):', error);
        }
    };

    const nextStep = async () => {
        // Validate current step
        const isStepValid = await trigger();
        if (isStepValid) {
            if (currentStep < STEPS.length) {
                setCurrentStep(prev => prev + 1);
                window.scrollTo(0, 0);
            } else {
                handleSubmit(onSubmit)();
            }
        }
    };

    const prevStep = () => {
        if (currentStep > 1) {
            setCurrentStep(prev => prev - 1);
            window.scrollTo(0, 0);
        } else {
            navigate(-1);
        }
    };

    const onSubmit = async (data) => {
        console.log('Final Financial Form Submitted:', data);
        try {
            await fetch('http://localhost:4000/api/kyc/submit', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('userToken')}`
                },
                body: JSON.stringify({
                    accountType: 'cross_border',
                    stepData: data,
                    sessionKey: sessionKey
                })
            });
            console.log('Submission successful (Mock)');
        } catch (error) {
            console.error('Failed to submit application (expected in mock):', error);
        }
        // Always navigate in mock mode
        navigate('/create/completed');
    };

    const progressPercent = ((currentStep - 1) / STEPS.length) * 100;

    return (
        <PageContainer>
            {/* --- DESKTOP SIDEBAR --- */}
            <SidebarContainer>
                <SidebarLogo src={logo} alt="NYBANQ" />
                <StepList>
                    {STEPS.map((step) => {
                        const isActive = currentStep === step.id;
                        const isCompleted = currentStep > step.id;

                        return (
                            <StepItem key={step.id} isActive={isActive} isCompleted={isCompleted}>
                                <StepIconWrapper isActive={isActive} isCompleted={isCompleted}>
                                    {isCompleted ? <Check size={16} strokeWidth={3} /> : step.id}
                                </StepIconWrapper>
                                <StepTextContent>
                                    <StepLabel isActive={isActive} isCompleted={isCompleted}>{step.title}</StepLabel>
                                    <StepDesc>{step.desc}</StepDesc>
                                </StepTextContent>
                            </StepItem>
                        );
                    })}
                </StepList>
            </SidebarContainer>

            {/* --- CONTENT AREA --- */}
            <ContentContainer>
                <MobileHeader>
                    <img src={logo} alt="NYBANQ" style={{
                        width: '100px',
                        objectFit: 'contain',
                        filter: theme.colors.background.paper !== '#ffffff' ? 'brightness(0) invert(1)' : 'none'
                    }} />
                    <MobileHeaderTitle>Step {currentStep} of {STEPS.length}</MobileHeaderTitle>
                </MobileHeader>

                <MobileProgressBar>
                    <MobileProgressFill progress={progressPercent + (100 / STEPS.length)} />
                </MobileProgressBar>

                <FormArea>
                    {/* Form Provider wraps the steps so they can access useFormContext */}
                    <FormProvider {...methods}>
                        <form onSubmit={(e) => e.preventDefault()}>
                            {currentStep === 1 && <Step1InstitutionProfile />}
                            {currentStep === 2 && <Step2ComplianceActivity />}
                            {currentStep === 3 && <Step3AuthorizedSigners />}
                            {currentStep === 4 && <Step4Documentation />}
                        </form>
                    </FormProvider>
                </FormArea>

                {/* Action Footer */}
                <ActionFooter>
                    {currentStep > 1 ? (
                        <GhostButton onClick={prevStep}>Back</GhostButton>
                    ) : (
                        <GhostButton onClick={() => navigate(-1)}>Cancel</GhostButton>
                    )}

                    <GhostButton onClick={saveDraft} style={{ marginLeft: 'auto', marginRight: '16px' }}>Save Draft</GhostButton>

                    <PrimaryButton onClick={nextStep}>
                        {currentStep === STEPS.length ? 'Submit Application' : 'Continue'}
                        {currentStep < STEPS.length && <ChevronRight size={18} />}
                    </PrimaryButton>
                </ActionFooter>
            </ContentContainer>
        </PageContainer>
    );
};

export default CrossBorderOnboarding;
